import React, { useState } from 'react';
import { useForm } from '../../context/FormContext';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { CreditCard, Edit3 } from 'lucide-react';

const FINANCE_TYPES = [
  { id: 'HP', label: 'Hire Purchase (HP)', description: 'Own the vehicle at the end of the agreement' },
  { id: 'PCP', label: 'Personal Contract Purchase (PCP)', description: 'Lower monthly payments with optional final payment' },
];

const HP_TERMS = [24, 36, 48, 60];
const PCP_TERMS = [24, 36, 48, 49];

export const FinanceTypeStep = () => {
  const { formData, updateFormData } = useForm();
  const financeType = formData.finance_type || 'HP';
  const commonTerms = financeType === 'HP' ? HP_TERMS : PCP_TERMS;
  const isCustomTerm = formData.term && !commonTerms.includes(formData.term);
  const [showCustom, setShowCustom] = useState(isCustomTerm);
  const [customValue, setCustomValue] = useState(isCustomTerm ? formData.term?.toString() : '');

  const handleFinanceTypeChange = (type) => {
    const newTerms = type === 'HP' ? HP_TERMS : PCP_TERMS;
    let newTerm = formData.term || 48;
    
    // If current term isn't in the new common terms and isn't custom, default to last common
    if (!newTerms.includes(newTerm) && !showCustom) {
      newTerm = newTerms[newTerms.length - 1];
    }
    // If switching to PCP and term > 49, cap it
    if (type === 'PCP' && newTerm > 49) {
      newTerm = 49;
    }
    
    updateFormData({ finance_type: type, term: newTerm });
  };

  const handleCustomTermChange = (value) => {
    const cleaned = value.replace(/\D/g, '');
    setCustomValue(cleaned);
    const num = parseInt(cleaned);
    if (num && num >= 1 && num <= 120) {
      updateFormData({ term: num });
    }
  };

  const selectCommonTerm = (months) => {
    setShowCustom(false);
    setCustomValue('');
    updateFormData({ term: months });
  };

  const enableCustom = () => {
    setShowCustom(true);
  };

  return (
    <div className="space-y-8" data-testid="finance-type-step">
      <div className="text-center">
        <div className="w-16 h-16 brand-bg-medium rounded-full hidden md:flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 brand-text" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Finance Type & Term
        </h2>
        <p className="text-slate-600 hidden md:block">Select your preferred finance option and agreement length</p>
      </div>

      {/* Finance Type Selection */}
      <div>
        <Label className="text-sm font-medium text-slate-700 mb-3 block">
          Finance Type <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FINANCE_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => handleFinanceTypeChange(type.id)}
              className={`p-5 rounded-xl border-2 transition-all text-left ${
                financeType === type.id
                  ? 'brand-border brand-bg-light'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`finance-type-${type.id.toLowerCase()}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  financeType === type.id ? 'brand-border' : 'border-slate-300'
                }`}>
                  {financeType === type.id && (
                    <div className="w-2.5 h-2.5 rounded-full brand-bg" />
                  )}
                </div>
                <div>
                  <span className={`font-semibold block ${
                    financeType === type.id ? 'brand-text' : 'text-slate-700'
                  }`}>
                    {type.label}
                  </span>
                  <span className="text-sm text-slate-500 mt-1 block">
                    {type.description}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Term Selection */}
      <div>
        <Label className="text-sm font-medium text-slate-700 mb-3 block">
          Agreement Term <span className="text-red-500">*</span>
        </Label>
        
        {/* Common term buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {commonTerms.map(months => (
            <button
              key={months}
              onClick={() => selectCommonTerm(months)}
              className={`py-5 px-4 rounded-xl border-2 text-center transition-all ${
                formData.term === months && !showCustom
                  ? 'brand-border brand-bg-light'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
              data-testid={`term-${months}`}
            >
              <span className={`text-2xl font-bold block ${
                formData.term === months && !showCustom ? 'brand-text' : 'text-slate-700'
              }`}>
                {months}
              </span>
              <span className="text-xs text-slate-500 mt-1 block">months</span>
            </button>
          ))}
        </div>

        {/* Custom term option */}
        {!showCustom ? (
          <button
            onClick={enableCustom}
            className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:brand-border hover:brand-text transition-all flex items-center justify-center gap-2 text-sm"
            data-testid="custom-term-btn"
          >
            <Edit3 className="w-4 h-4" />
            Different term length?
          </button>
        ) : (
          <div className="p-4 rounded-xl border-2 brand-border brand-bg-light">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">
              Select term (months)
            </Label>
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 max-h-48 overflow-y-auto p-1">
              {Array.from({ length: 57 }, (_, i) => i + 12).map(m => (
                <button
                  key={m}
                  onClick={() => {
                    updateFormData({ term: m });
                    setCustomValue(m.toString());
                  }}
                  className={`py-2 px-1 rounded-lg text-sm font-medium transition-all ${
                    formData.term === m
                      ? 'brand-bg text-white'
                      : 'bg-white border border-slate-200 text-slate-700 hover:brand-border'
                  }`}
                  data-testid={`custom-term-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setShowCustom(false); }}
              className="mt-3 text-xs text-slate-500 hover:brand-text transition-colors"
            >
              Back to common terms
            </button>
          </div>
        )}

        <div className="mt-4 text-center">
          <span className="text-lg font-semibold text-slate-900">
            Selected: <span className="brand-text">{formData.term || 48} months</span>
          </span>
        </div>
      </div>

      {/* Annual Mileage - PCP only */}
      {financeType === 'PCP' && (
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-3 block">
            Estimated Annual Mileage <span className="text-red-500">*</span>
          </Label>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
            {Array.from({ length: 20 }, (_, i) => (i + 5) * 1000).map(miles => (
              <button
                key={miles}
                onClick={() => updateFormData({ annual_mileage: miles })}
                className={`py-3 px-2 rounded-lg border-2 text-center transition-all ${
                  formData.annual_mileage === miles
                    ? 'brand-border brand-bg-light'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
                data-testid={`mileage-${miles}`}
              >
                <span className={`text-sm font-semibold ${
                  formData.annual_mileage === miles ? 'brand-text' : 'text-slate-700'
                }`}>
                  {miles.toLocaleString()}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-3 text-center">
            <span className="text-sm text-slate-600">
              Selected: <span className="font-semibold brand-text">{(formData.annual_mileage || 10000).toLocaleString()} miles/year</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceTypeStep;
